/**
 * The "Submit check-in" click, as plain logic (no React, no imports) so it can
 * be tested on its own (submitFlow.test.ts).
 *
 * ONE SUBMIT AT A TIME. `inFlight` is the screen's useRef: it is set
 * synchronously, before anything is awaited, so a second click that arrives
 * before React has re-rendered the disabled button is ignored — the `busy`
 * state alone cannot do that, because it only takes effect on the next
 * render.
 *
 * ALREADY SUBMITTED IS SUCCESS. The server answers a repeated submit with 200,
 * but a write that finds the check-in already submitted (the ETA save after
 * another tab submitted, or an older server) answers 409 with code
 * "already_submitted". That means the guest is done: show the success
 * screen, never send them back to the ETA step.
 */

export interface SubmitFlowHandlers {
  /** Called once the guard is taken — e.g. mark the button busy, clear the error. */
  begin: () => void;
  saveEta: () => Promise<unknown>;
  /** Between the ETA save and the submit — e.g. show the "submitting" step. */
  submitting: () => void;
  submit: () => Promise<unknown>;
  /** The check-in is done. `result` is the submit's body, or null when the server said it already was. */
  succeeded: (result: unknown | null) => void;
  /** The server named a wizard step with something missing (412). */
  goToStep: (step: string, message: string) => void;
  failed: (message: string) => void;
  isKnownStep: (step: string | null) => boolean;
  /** Always called at the end of a run that was not ignored. */
  end: () => void;
}

interface ErrorLike {
  status?: unknown;
  message?: unknown;
  body?: { code?: unknown; step?: unknown } | null;
}

/** Runs one submit, or returns "ignored" at once if one is already running. */
export async function runSubmitOnce(
  inFlight: { current: boolean },
  h: SubmitFlowHandlers
): Promise<"ran" | "ignored"> {
  if (inFlight.current) return "ignored";
  inFlight.current = true;
  h.begin();
  try {
    await h.saveEta();
    h.submitting();
    const result = await h.submit();
    h.succeeded(result);
  } catch (err) {
    const e = (err ?? {}) as ErrorLike;
    const code = typeof e.body?.code === "string" ? e.body.code : null;
    const step = typeof e.body?.step === "string" ? e.body.step : null;
    const message = typeof e.message === "string" && e.message !== "" ? e.message : "Something went wrong";
    if (e.status === 409 && code === "already_submitted") {
      h.succeeded(null);
    } else if (e.status === 412 && h.isKnownStep(step)) {
      h.goToStep(step as string, `${message} — take a moment to complete that step.`);
    } else {
      h.failed(message);
    }
  } finally {
    inFlight.current = false;
    h.end();
  }
  return "ran";
}
